import { networkDebugMonitor } from "@/services/monitoring/network-debug-monitor/network-debug-monitor";

/**
 * System Config Service
 *
 * Fetches system configuration from the radar device.
 * Endpoint: http://<radar_ip>:8888/api/system_config (port overridable, see
 * radar-port-override.ts, for local testing against emulators on other ports)
 *
 * Features:
 * - Fetches system config from radar device
 * - Supports ETag-based caching (If-None-Match)
 * - Handles 304 Not Modified responses
 */
import type { SystemConfigFetchResult } from "../types/system-config-fetch-result";
import {
  buildSystemConfigUrl,
  radarPortOverrideStorage,
} from "./radar-port-override/radar-port-override";

const SERVICE_NAME = "system-config";

class SystemConfigService {
  private lastEtagByRadarId = new Map<string, string>();

  /**
   * Fetch system configuration from radar device
   *
   * @param radarHttpBaseUrl - The radar HTTP base URL (e.g., http://192.168.1.100)
   * @returns A discriminated result distinguishing success, not-modified, no-url and error
   */
  async fetchSystemConfig(
    radarHttpBaseUrl: string,
    radarId: string
  ): Promise<SystemConfigFetchResult> {
    if (!radarHttpBaseUrl) {
      console.error("❌ [SystemConfig] No radar HTTP base URL provided");
      return { status: "no-url" };
    }

    const portOverride = await radarPortOverrideStorage.get();
    const url = buildSystemConfigUrl(radarHttpBaseUrl, portOverride);
    const headers: HeadersInit = {};

    // Add If-None-Match header if we have a previous ETag for this radar
    const previousEtag = this.lastEtagByRadarId.get(radarId);
    if (previousEtag) {
      headers["If-None-Match"] = previousEtag;
      console.log(`🔍 [SystemConfig] Using If-None-Match: ${previousEtag}`);
    }

    const startedAt = performance.now();

    try {
      console.log(`🌐 [SystemConfig] Fetching system config from: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers,
        mode: "cors",
        // No credentials needed - this endpoint doesn't require login
      });

      let responseBody: string | undefined;
      if (networkDebugMonitor.isActive()) {
        try {
          responseBody = await response.clone().text();
        } catch {
          // Best-effort for the debug console only.
        }
      }

      networkDebugMonitor.logHttpRequest({
        service: SERVICE_NAME,
        method: "GET",
        url,
        status: response.status,
        durationMs: performance.now() - startedAt,
        responseBody,
        radarId,
      });

      // 304 Not Modified - config hasn't changed
      if (response.status === 304) {
        console.log(
          "✅ [SystemConfig] 304 Not Modified - using existing config"
        );
        return { status: "not-modified" };
      }

      // Handle non-200 responses
      if (!response.ok) {
        console.error(
          `❌ [SystemConfig] Unexpected status code: ${response.status}`
        );
        return { status: "error", error: `HTTP ${response.status}` };
      }

      // Store new ETag for future requests to this radar
      const etag = response.headers.get("ETag");
      if (etag) {
        this.lastEtagByRadarId.set(radarId, etag);
        console.log(`💾 [SystemConfig] Stored ETag: ${etag}`);
      }

      // Parse and return the config data
      const data = await response.json();
      console.log("✅ [SystemConfig] Successfully fetched system config");
      console.log("📋 [SystemConfig] Config data:", data);

      return { status: "ok", config: data };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      networkDebugMonitor.logHttpRequest({
        service: SERVICE_NAME,
        method: "GET",
        url,
        durationMs: performance.now() - startedAt,
        error: errorMessage,
        radarId,
      });
      console.error("❌ [SystemConfig] Failed to fetch system config:", error);
      return { status: "error", error: errorMessage };
    }
  }

  /**
   * Clear stored ETag for a radar (useful for forcing a fresh fetch)
   */
  clearEtag(radarId: string): void {
    this.lastEtagByRadarId.delete(radarId);
    console.log(`🗑️ [SystemConfig] Cleared ETag for radar ${radarId}`);
  }
}

// Export singleton instance
export const systemConfigService = new SystemConfigService();
